<script>
    import filter from "svelte-select/filter";
    import { Button } from "./index.svelte";
    import { v4 } from "uuid";
    import { onDestroy } from "svelte";

    export let isModalOpen = false,
        title = "Note",
        titleContent = null,
        cancelText = "Close",
        submitText = "Submit",
        modalMaxWidth = "",
        closeModal = () => {},
        confirmAction = () => {},
        isConfirming = false,
        isCancelDisabled = false,
        isConfirmDisabled = false,
        showCancelBtn = true,
        showConfirmBtn = true,
        isReverseBtns = false,
        isColouredTitle = true,
        transformTitleCase = true,
        btnOptions = [],
        headerOptions = [],
        zIndex = 9999,
        hideCrossButton = false,
        preventESCClose = false,
        closeModalFunction,
        confirmButtonClass = "",
        noContentPadding = false,
        cancelButtonClass = "btn-error",
        contentClass = null,
        fullWidthOnSmallerScreens = false,
        customizeId = "",
        hideActions = false;

    let modalBoxElement;
    let modalPosition = null;
    let dragPointerId = null;
    let dragOffset = { x: 0, y: 0 };

    let previousKeyDown,
        closeWithEscEnabled = false;

    $: if (isModalOpen && !preventESCClose) {
        enableCloseWithESC();
    } else {
        closeWithEscEnabled = false;
        if (previousKeyDown) document.onkeydown = previousKeyDown;
        previousKeyDown = undefined;
    }

    $: if (!isModalOpen) {
        releaseDragListeners();
        dragPointerId = null;
        modalPosition = null;
    }

    const enableCloseWithESC = () => {
        if (closeWithEscEnabled) return;
        closeWithEscEnabled = true;
        previousKeyDown = document.onkeydown;
        document.onkeydown = (event) => {
            if (isConfirming) return;
            if (event.key === "Escape")
                closeModalFunction && closeModalFunction();
        };
    };

    const onCrossClick = (e) => closeModalFunction && closeModalFunction(e);

    const handleClickHeaderOption = (option) => {
        const { isDisabled } = option;

        if (isDisabled) return;

        option.function();
    };

    const handleDragIconPointerDown = (event) => {
        if (!isModalOpen || !modalBoxElement) return;
        if (event.button && event.button !== 0) return;
        dragPointerId = event.pointerId;
        const rect = modalBoxElement.getBoundingClientRect();
        modalPosition = clampWithinViewport(rect.left, rect.top, rect.width, rect.height);
        dragOffset = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };
        event.preventDefault();
        addDragListeners();
    };

    const handlePointerMove = (event) => {
        if (dragPointerId === null || event.pointerId !== dragPointerId || !modalBoxElement)
            return;
        event.preventDefault();
        const proposedLeft = event.clientX - dragOffset.x;
        const proposedTop = event.clientY - dragOffset.y;
        updateModalPosition(proposedLeft, proposedTop);
    };

    const handlePointerUp = (event) => {
        if (dragPointerId === null || (event && event.pointerId !== dragPointerId)) return;
        dragPointerId = null;
        releaseDragListeners();
    };

    const addDragListeners = () => {
        if (typeof window === "undefined") return;
        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);
    };

    const releaseDragListeners = () => {
        if (typeof window === "undefined") return;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
    };

    const updateModalPosition = (left, top) => {
        if (!modalBoxElement) return;
        const { width, height } = modalBoxElement.getBoundingClientRect();
        modalPosition = clampWithinViewport(left, top, width, height);
    };

    const clampWithinViewport = (left, top, width, height) => {
        if (typeof window === "undefined") {
            return { left, top };
        }

        const maxLeft = Math.max(0, window.innerWidth - width);
        const maxTop = Math.max(0, window.innerHeight - height);

        return {
            left: Math.min(Math.max(0, left), maxLeft),
            top: Math.min(Math.max(0, top), maxTop)
        };
    };

    onDestroy(() => {
        releaseDragListeners();
    });

    $: modalInlineStyle = modalPosition
        ? `position: fixed; top: ${modalPosition.top}px; left: ${modalPosition.left}px; margin: 0;`
        : "";
</script>

{#if isModalOpen}
    <div
        class={isModalOpen ? `modal modal-open z-[${zIndex}]` : "modal"}
        id={customizeId ? customizeId : v4()}
    >
        <div
            class={`modal-box no-transform overflow-visible p-0 flex flex-col ${
                fullWidthOnSmallerScreens ? "fullWidthOnSmallerScreens" : ""
            } ${modalMaxWidth}`}
            bind:this={modalBoxElement}
            style={modalInlineStyle}
        >
            <div
                class={`flex w-full items-center justify-between font-medium text-lg rounded-tl-xl rounded-tr-xl header-margin py-3 pl-6 pr-1 ${
                    isColouredTitle
                        ? "text-[#fff] flex-0 bg-[#8a3ed7] px-6 py-3 min-h-[50px] "
                        : ""
                }`}
            >
                <div class="flex items-center gap-3">
                    <button
                        class="drag-handle btn btn-circle btn-ghost btn-xs text-base"
                        type="button"
                        aria-label="Drag modal"
                        on:pointerdown={handleDragIconPointerDown}
                    >
                        <i class="fa fa-arrows" aria-hidden="true" />
                    </button>
                    <p class={transformTitleCase ? "capitalize" : "normal-case"}>
                        {#if $$slots.title}
                            <slot name="title" />
                        {:else}
                            {title}
                        {/if}
                        {#if titleContent}
                            <svelte:component this={titleContent.component} {...titleContent.props} />
                        {/if}
                    </p>
                </div>

                <div class="flex flex-row">
                    {#if headerOptions.length}
                        <div class="flex items-center gap-x-6 mr-6">
                            {#each headerOptions as headerOption}
                                {#if !headerOption.isHidden}
                                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                                    <div class={`flex items-center gap-x-2 ${headerOption.iconName}`}>
                                        {#if headerOption.tooltipContent}
                                            <!-- Render a component passed via headerOption.tooltipContent -->
                                            <svelte:component
                                                this={headerOption.tooltipContent.component}
                                                {...(headerOption.tooltipContent.props || {})}
                                            />
                                        {:else}
                                            <div
                                                class={`flex items-center gap-x-2 ${headerOption.isDisabled ? "cursor-not-allowed" : "cursor-pointer"} ${(headerOption.showAsTooltip  || (headerOption.isDisabled && headerOption.disabledTooltip)) ? "tooltip tooltip-secondary tooltip-bottom" : "" }`}
                                                data-tip={(headerOption.showAsTooltip  || (headerOption.isDisabled && headerOption.disabledTooltip))
                                                    ? headerOption.disabledTooltip ?? headerOption.optionText
                                                    : ""}
                                                on:click|preventDefault={() => handleClickHeaderOption(headerOption)}
                                            >
                                                {#if headerOption.iFont}
                                                    <i class={`text-[16px] ${headerOption.iconName} ${headerOption.iFont}`} />
                                                {/if}
                                                {#if !headerOption.showAsTooltip}
                                                    <p class={`${headerOption.iconName}`}>
                                                        {headerOption.optionText}
                                                    </p>
                                                {/if}
                                            </div>
                                        {/if}
                                    </div>
                                {/if}
                            {/each}
                        </div>
                    {/if}

                    {#if !hideCrossButton && closeModalFunction}
                        <button
                            class="btn btn-circle btn-sm btn-ghost"
                            on:click={onCrossClick}
                        >
                            <i class="fa fa-times" aria-hidden="true"></i>
                        </button>
                    {/if}
                </div>
            </div>

            {#if noContentPadding}
                <slot />
            {:else}
                <div class={contentClass ?? `overflow-y-auto oveflow-x-hidden p-6 flex flex-col`}>
                    <slot />
                </div>
            {/if}

            {#if !hideActions}
                <div
                    class="modal-action flex items-center justify-end gap-x-4 px-6 pb-6 pt-4 mt-0"
                >
                    <slot name="beforeButtonsSection" />
                    <div
                        class="flex items-center gap-x-4 {isReverseBtns
                            ? 'flex-row-reverse'
                            : ''}"
                    >
                        {#if showCancelBtn}
                            <Button
                                manualClass="{cancelButtonClass} min-w-[100px] {isConfirming
                                    ? 'loading'
                                    : ''}"
                                isDisabled={isConfirming || isCancelDisabled}
                                clickFunc={closeModal}
                            >
                                {cancelText}
                            </Button>
                        {/if}

                        {#if showConfirmBtn}
                            <Button
                                manualClass="{confirmButtonClass} min-w-[100px] {isConfirming
                                    ? 'loading'
                                    : ''}"
                                isDisabled={isConfirming || isConfirmDisabled}
                                clickFunc={confirmAction}
                            >
                                {submitText}
                            </Button>
                        {/if}
                    </div>

                    {#each btnOptions.filter((b) => b != null) as btn}
                        {#if !btn.isHidden}
                            <Button
                                manualClass="min-w-[100px] {btn.class
                                    ? btn.class
                                    : ''} {isConfirming ? 'loading' : ''}"
                                isDisabled={btn.loading || btn.isDisabled}
                                clickFunc={() => btn.function()}
                                disabledTooltip={btn.tooltip}
                            >
                                {btn.text}
                            </Button>
                        {/if}
                    {/each}
                </div>
            {/if}
           
        </div>
    </div>
{/if}

<style>
    @media only screen and (max-width: 992px) {
        .fullWidthOnSmallerScreens {
            max-width: 95vw;
        }
    }

    .drag-handle {
        cursor: move;
        color: inherit;
    }
</style>
